import { createFileRoute, Link, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { getStoredAuth } from "@/lib/api";
import { useState, useEffect, useCallback } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { useAuth, type Permission } from "@/context/AuthContext";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

const ADMIN_ROLES: readonly string[] = [
  "super_admin",
  "moderator",
  "venue_manager",
  "hotel_owner",
  "event_centre_owner",
  "open_space_owner",
  "resort_owner",
  "hotel_event_owner",
  "apartment_owner",
  "conference_hall_owner",
];

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
  head: () => ({
    meta: [
      { title: "Admin — OyoBooking" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: ({ location }) => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (!auth) throw redirect({ to: "/login", search: { redirect: location.pathname } });
    if (!auth.role || !ADMIN_ROLES.includes(auth.role)) {
      throw redirect({ to: "/login", search: { redirect: location.pathname } });
    }
  },
});

function AdminLayout() {
  const { isAuthenticated, role, hasPermission, isBusinessOwner, isModerator, isSuperAdmin } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Auto-collapse sidebar on tablet-width screens (1024–1279px, e.g. iPad Pro 12.9")
  const isTabletWidth = useCallback(
    () => typeof window !== "undefined" && window.innerWidth >= 1024 && window.innerWidth < 1280,
    [],
  );
  const [collapsed, setCollapsed] = useState(isTabletWidth);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setCollapsed(isTabletWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isTabletWidth]);

  // Client-side fallback guard (covers hydration)
  if (typeof window !== "undefined" && (!isAuthenticated || !role || !ADMIN_ROLES.includes(role))) {
    // Render nothing while redirect resolves
    return null;
  }

  const routeAccess = getRouteAccess(pathname);
  const canAccess =
    isSuperAdmin ||
    !routeAccess ||
    (routeAccess.kind === "permission" && hasPermission(routeAccess.permission)) ||
    (routeAccess.kind === "business" && isBusinessOwner) ||
    (routeAccess.kind === "moderation" && (isModerator || hasPermission("venues.moderate")));

  return (
    <div className="admin-dashboard flex h-screen w-full overflow-hidden bg-muted/30">
      <AdminSidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <AdminHeader
          onToggleSidebar={() => setCollapsed((c) => !c)}
          onOpenMobile={() => setMobileOpen(true)}
        />
        <main className="flex-1 overflow-y-auto">
          {canAccess ? <Outlet /> : <AccessDenied />}
        </main>
      </div>
    </div>
  );
}

type RouteAccess =
  | { kind: "permission"; permission: Permission }
  | { kind: "business" }
  | { kind: "moderation" };

function getRouteAccess(pathname: string): RouteAccess | null {
  if (pathname.startsWith("/admin/analytics")) return { kind: "permission", permission: "reports.access" };
  if (pathname.startsWith("/admin/venues")) return { kind: "permission", permission: "venues.manage_all" };
  if (pathname.startsWith("/admin/customers")) return { kind: "permission", permission: "users.view" };
  if (pathname.startsWith("/admin/venue-managers")) return { kind: "permission", permission: "users.view" };
  if (pathname.startsWith("/admin/staff")) return { kind: "permission", permission: "users.manage" };
  if (pathname.startsWith("/admin/pending-payouts")) return { kind: "permission", permission: "bookings.manage" };
  if (pathname.startsWith("/admin/support")) return { kind: "permission", permission: "bookings.manage" };
  if (pathname.startsWith("/admin/refunds")) return { kind: "permission", permission: "bookings.manage" };
  if (pathname.startsWith("/admin/withdrawals")) return { kind: "permission", permission: "bookings.manage" };
  if (pathname.startsWith("/admin/settings")) return { kind: "permission", permission: "settings.manage" };
  if (pathname.startsWith("/admin/roles")) return { kind: "permission", permission: "admins.manage" };
  if (pathname.startsWith("/admin/moderator")) return { kind: "moderation" };
  if (pathname.startsWith("/admin/listings")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/rooms")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/calendar")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/manager-analytics")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/escrows")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/earnings")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/reviews")) return { kind: "permission", permission: "venues.manage_assigned" };
  if (pathname.startsWith("/admin/check-in")) return { kind: "permission", permission: "bookings.manage" };
  if (pathname.startsWith("/admin/scan-tickets")) return { kind: "permission", permission: "bookings.manage" };
  if (pathname.startsWith("/admin/bookings")) return { kind: "permission", permission: "bookings.view" };
  if (pathname.startsWith("/admin/events")) return { kind: "permission", permission: "bookings.view" };
  if (pathname.startsWith("/admin/wallet")) return { kind: "business" };
  return null;
}

function AccessDenied() {
  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="max-w-md rounded-2xl border border-border/60 bg-card p-6 text-center shadow-soft">
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="size-6" />
        </div>
        <h1 className="mt-4 font-display text-xl font-bold">You do not have access</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This dashboard area is not available for your current role.
        </p>
        <Link to="/admin/dashboard">
          <Button className="mt-5">Back to dashboard</Button>
        </Link>
      </div>
    </div>
  );
}
